/**
 * The Recommend model's own ceiling, not the number of items we display.
 *
 * `queryParameters.filters` is applied *after* the model picks its candidates,
 * so asking for exactly as many as we render would routinely come back short —
 * or empty — once the filter removes the ones whose editions fall outside the
 * window. We over-fetch and cut the list down ourselves.
 */
const MAX_RECOMMENDATIONS = 30;
/**
 * Fetches trending items, optionally per facet value, and merges them into one
 * ranked list.
 *
 * Hits are ordered by the model's `_score` across all requests, de-duplicated by
 * `objectID`, then cut to `limit`.
 *
 * @param client - The un-patched Algolia client owned by the runtime.
 * @param indexName - The physical index name.
 * @param query - The lookup options.
 * @returns The merged hits, at most `limit` of them. Empty when the model has
 *   no data yet — trending items are derived from collected conversion events,
 *   so an index with no events returns nothing rather than failing.
 */
export async function fetchTrendingItems(client, indexName, query) {
    const { facetName, facetValues, filters, limit, threshold = 0 } = query;
    if (limit <= 0)
        return [];
    const scopes = facetName && facetValues?.length ? facetValues.map((value) => value) : [undefined];
    const requests = scopes.map((facetValue) => ({
        indexName,
        model: 'trending-items',
        threshold,
        maxRecommendations: MAX_RECOMMENDATIONS,
        ...(facetName && facetValue !== undefined ? { facetName, facetValue } : {}),
        ...(filters ? { queryParameters: { filters } } : {})
    }));
    const { results } = await client.getRecommendations({ requests });
    const seen = new Set();
    const merged = [];
    for (const hit of results
        .flatMap((result) => result.hits)
        .sort((a, b) => (b._score ?? 0) - (a._score ?? 0))) {
        if (seen.has(hit.objectID))
            continue;
        seen.add(hit.objectID);
        merged.push(hit);
        if (merged.length === limit)
            break;
    }
    return merged;
}
/**
 * Creates the Recommend client lazily, on first use.
 *
 * Recommend is only reached on the zero-result path, so building the client
 * eagerly in `createAlgolia` would cost every app that never hits it.
 *
 * @param client - The un-patched Algolia client owned by the runtime.
 * @returns A function returning the memoised Recommend client.
 */
export function lazyRecommendClient(client) {
    let recommend;
    return () => (recommend ??= client.initRecommend());
}
